import { NavigationLinks } from 'rentalui-maps';
import { useState } from 'react';
import { Button, Modal } from 'react-bootstrap';

// "Show on Google Maps" - an on-demand map popup.
//
// Google Maps is NEVER loaded until the user clicks: no <iframe>, no Maps JS, no /api/maps call
// on a list / search / detail render. The embed below is keyless
// (google.com/maps?q=...&output=embed) and is only mounted while the modal is open, so closing
// it tears the iframe down again. The "Open in Google Maps" link is a plain google.com/maps
// deep link (also keyless) - what a delivery person taps for turn-by-turn.
//
// Every coordinate / locality / city comes from whatever RentalVerse already stored on the
// address - this component never geocodes. If there is nothing to point at, it renders nothing.
// See Docs/requirements/common/v3/maps_on_demand_and_location_reuse.md.

function joinAddress(a) {
  if (!a) return '';
  return [a.line1, a.line2, a.area, a.city, a.state, a.pincode]
    .map((p) => (p == null ? '' : String(p).trim()))
    .filter(Boolean)
    .join(', ');
}

// The map query, using only already-stored fields (no geocoding): exact coordinates when the
// address was pinned, else Google's stored formatted string, else the structured parts, else
// the caller's free-text fallback.
export function mapQueryFor(address, fallbackText = '') {
  const a = address || {};
  const hasCoords = a.lat != null && a.lat !== '' && a.lng != null && a.lng !== '';
  if (hasCoords) return `${a.lat},${a.lng}`;
  return a.formatted || joinAddress(a) || String(fallbackText || '').trim();
}

// 03_requirements_missing.md story 9 + v5_fix_issues #2 - the customer-facing map shows a
// ~100 m area, not the exact door. When coordinates are known, round them to 3 dp (~110 m grid)
// and drop a pin there; otherwise fall back to the locality text (area / city / state / pincode).
const APPROX_DP = 3;
function roundApprox(n) {
  const f = 10 ** APPROX_DP;
  return Math.round(Number(n) * f) / f;
}
function hasCoords(a) {
  return a && a.lat != null && a.lat !== '' && a.lng != null && a.lng !== '';
}

function approxAreaText(address, fallbackText = '') {
  const a = address || {};
  const parts = [a.area, a.city, a.state, a.pincode].map((p) => (p == null ? '' : String(p).trim())).filter(Boolean);
  return parts.join(', ') || String(fallbackText || '').trim();
}

export function approxMapQueryFor(address, fallbackText = '') {
  const a = address || {};
  if (hasCoords(a)) return `${roundApprox(a.lat)},${roundApprox(a.lng)}`;
  return approxAreaText(a, fallbackText);
}

export default function MapLink({
  address,
  fallbackText = '',
  query: queryProp,
  label,
  title = 'Location',
  variant = 'link',
  size = 'sm',
  className = '',
  approximate = false
}) {
  const [open, setOpen] = useState(false);

  const approx = approximate || !!address?.approximate;
  const resolvedLabel = label || (approx ? '📍 Show approximate area' : '📍 Show on Google Maps');

  const query = (queryProp && String(queryProp).trim())
    || (approx ? approxMapQueryFor(address, fallbackText) : mapQueryFor(address, fallbackText));
  if (!query) return null;

  const line = approx
    ? (hasCoords(address) ? '~100 m area' : approxAreaText(address, fallbackText))
    : (address?.formatted || joinAddress(address) || fallbackText || '');
  // v5_fix_issues #2 - a ~100 m area zooms in close (z16) when we have coords; the text-only
  // fallback stays neighbourhood-level (z13).
  const zoom = approx ? (hasCoords(address) ? 16 : 13) : 15;
  const embedSrc = `https://www.google.com/maps?q=${encodeURIComponent(query)}&z=${zoom}&output=embed`;
  const openSrc = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;

  return (
    <>
      <Button
        variant={variant}
        size={size}
        className={variant === 'link' ? `p-0 align-baseline ${className}`.trim() : className}
        onClick={() => setOpen(true)}
      >
        {resolvedLabel}
      </Button>

      <Modal show={open} onHide={() => setOpen(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title as="h6">{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {approx && (
            <p className="small mb-2">
              <span className="badge bg-secondary me-1">Approximate</span>
              This shows the general area only. The exact address is shared with you (and the
              visiting agent) once your site visit is confirmed.
            </p>
          )}
          {line && <p className="text-muted small mb-2">{line}</p>}
          {/* Mounted only while the modal is open - this is the single Maps load, on demand. */}
          {open && (
            <iframe
              title={title}
              src={embedSrc}
              width="100%"
              height={320}
              style={{ border: 0, borderRadius: 8 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <NavigationLinks query={query} approximate={approx} />
          <a className="btn btn-primary btn-sm" href={openSrc} target="_blank" rel="noreferrer">
            Open in Google Maps ↗
          </a>
        </Modal.Footer>
      </Modal>
    </>
  );
}
